import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-purple-500/30 rounded-xl p-3 shadow-xl min-w-[160px]">
      <p className="text-purple-300 text-xs font-medium mb-2">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry?.color }} />
            <span className="text-purple-200 text-xs">{entry?.name}</span>
          </div>
          <span className="text-white text-xs font-bold">
            {entry?.name === 'Completion Rate' ? `${entry?.value}%` : entry?.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const TrendingChart = ({ data, loading, dateRange }) => {
  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="h-5 bg-white/20 rounded animate-pulse w-48 mb-6" />
        <div className="h-64 bg-white/10 rounded-xl animate-pulse" />
      </div>
    );
  }

  const hasData = (data || [])?.some(d => d?.watchEvents > 0);

  // Downsample for 90-day view to avoid crowding
  let displayData = data || [];
  if (dateRange === 90 && displayData?.length > 30) {
    // Show every 3rd day
    displayData = displayData?.filter((_, i) => i % 3 === 0);
  } else if (dateRange === 30 && displayData?.length > 30) {
    displayData = displayData?.filter((_, i) => i % 2 === 0);
  }

  // Summary stats
  const totalEvents = (data || [])?.reduce((s, d) => s + d?.watchEvents, 0);
  const avgCompletion = hasData
    ? Math.round((data || [])?.filter(d => d?.watchEvents > 0)?.reduce((s, d) => s + d?.completionRate, 0) / ((data || [])?.filter(d => d?.watchEvents > 0)?.length || 1))
    : 0;

  // Trend direction
  const recentHalf = (data || [])?.slice(Math.floor((data?.length || 0) / 2));
  const earlyHalf = (data || [])?.slice(0, Math.floor((data?.length || 0) / 2));
  const recentAvg = recentHalf?.length > 0 ? recentHalf?.reduce((s, d) => s + d?.watchEvents, 0) / recentHalf?.length : 0;
  const earlyAvg = earlyHalf?.length > 0 ? earlyHalf?.reduce((s, d) => s + d?.watchEvents, 0) / earlyHalf?.length : 0;
  const trendUp = recentAvg >= earlyAvg;

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <TrendingUp className="w-4 h-4 text-blue-300" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Trending Analysis</h2>
            <p className="text-purple-300 text-xs mt-0.5">
              Watch events &amp; completion rates over the last {dateRange} days
            </p>
          </div>
        </div>
        {/* Summary pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-white/10 rounded-xl px-3 py-1.5 text-center">
            <p className="text-white text-sm font-bold">{totalEvents?.toLocaleString()}</p>
            <p className="text-purple-300 text-xs">Total Events</p>
          </div>
          <div className="bg-white/10 rounded-xl px-3 py-1.5 text-center">
            <p className="text-white text-sm font-bold">{avgCompletion}%</p>
            <p className="text-purple-300 text-xs">Avg Completion</p>
          </div>
          <div className={`rounded-xl px-3 py-1.5 text-center ${trendUp ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
            <p className={`text-sm font-bold ${trendUp ? 'text-emerald-300' : 'text-red-300'}`}>
              {trendUp ? '↑ Up' : '↓ Down'}
            </p>
            <p className="text-purple-300 text-xs">Trend</p>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-64 text-purple-300">
          <TrendingUp className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-lg font-medium">No trending data yet</p>
          <p className="text-sm mt-1">Watch events will appear here as students engage with videos</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={displayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#a78bfa', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: '#a78bfa', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#6ee7b7', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              domain={[0, 100]}
              tickFormatter={v => `${v}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ color: '#c4b5fd', fontSize: 12, paddingTop: 12 }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="watchEvents"
              name="Watch Events"
              stroke="#818cf8"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#818cf8' }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="completionRate"
              name="Completion Rate"
              stroke="#34d399"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#34d399' }}
              strokeDasharray="4 2"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TrendingChart;
